import { faCheck, faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { differenceInDays, fromUnixTime } from 'date-fns';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Card, CardActionArea, CardContent } from 'src/components/Card';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { LeaderboardResultItem } from 'src/models/leaderboard/_types';
import { LogoImage } from 'src/shared/LogoImage';

interface LeaderboardCardProps {
  name: string;
  image?: string;
  header?: string;
  subHeader?: string;
  result?: LeaderboardResultItem | null;
  onClick: () => void;
  actions?: ReactNode;
}

export const LeaderboardCard = ({
  name,
  image,
  header,
  subHeader,
  result,
  onClick,
  actions,
}: LeaderboardCardProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const dateString = result?.date
    ? differenceInDays(new Date(), fromUnixTime(result.date))
    : null;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: '50%', lg: '33.33%' },
        px: 2,
        pb: 2,
        position: 'relative',
        display: 'flex',
      }}
    >
      {result && (
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'success.main',
            position: 'absolute',
            top: -6,
            right: 10,
            zIndex: 1,
          }}
        >
          <Icon icon={faCheck} color="inherit" size="tiny" />
        </Box>
      )}
      <Card
        sx={{
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardActionArea
          onClick={onClick}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            flexGrow: 1,
          }}
        >
          {mdUp ? (
            image ? (
              <Box
                component="img"
                src={image}
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                }}
              />
            ) : (
              <LogoImage height={200} width="100%" />
            )
          ) : null}
          <CardContent sx={{ py: 2, pl: 2, pr: 1, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!mdUp &&
                (image ? (
                  <Box
                    component="img"
                    src={image}
                    sx={{
                      height: 60,
                      width: 72,
                      objectFit: 'contain',
                      borderRadius: 1,
                      mr: 2,
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box sx={{ mr: 2, flexShrink: 0 }}>
                    <LogoImage height={60} width={72} />
                  </Box>
                ))}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Box>
                  {header && <Typography variant="body2">{header}</Typography>}
                  <Typography variant="body1">{name}</Typography>
                  {subHeader && (
                    <Typography variant="body2" color="text.secondary">
                      {subHeader}
                    </Typography>
                  )}
                </Box>
                {actions && (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu
                      trigger={
                        <IconButton size="small">
                          <Icon icon={faEllipsisV} />
                        </IconButton>
                      }
                    >
                      {actions}
                    </DropdownMenu>
                  </Box>
                )}
              </Box>
            </Box>
            {result?.value && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    src={result.thumbnail}
                    sx={{ width: 28, height: 28 }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1.5 }}
                  >
                    {t('resultDate', { days: dateString })}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ pr: 1 }}>
                  {result.value}
                </Typography>
              </Box>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  );
};
